import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useTranslation } from 'react-i18next';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../context/AuthContext';
import { getFirebaseErrorKey } from '../utils/firebaseErrors';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const { t } = useTranslation();
  const { signup, loginWithGoogle } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!name || !email || !password || !confirmPassword) {
      setError(t('auth.fillAllFields'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDontMatch'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setError(t('legal.ageRestriction'));
      setLoading(false);
      return;
    }

    try {
      await signup(email, password, name);
      navigate(returnTo);
    } catch (err) {
      setError(t(getFirebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      navigate(returnTo);
    } catch (err) {
      setError(t(getFirebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>{t('auth.register')}</h2>
        <p className="auth-subtitle">{t('hero.subtitle')}</p>

        {returnTo !== '/' && (
          <div style={{
            background: 'rgba(108, 92, 231, 0.08)',
            border: '1px solid rgba(108, 92, 231, 0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: 'var(--primary)',
            textAlign: 'center',
          }}>
            🔄 Após criar sua conta, você será redirecionado de volta para continuar sua compra
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{t('auth.name')}</label>
            <input
              id="name"
              type="text"
              className="form-control"
              placeholder="João Silva"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              className="form-control"
              placeholder="voce@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                id="password"
                type="password"
                className="form-control"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-control"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Terms & Privacy + Age Acceptance */}
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.6rem',
            cursor: 'pointer',
            fontSize: '0.85rem',
            lineHeight: 1.5,
            color: 'var(--dark-light)',
            marginBottom: '1rem',
            padding: '0.5rem 0',
          }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{
                marginTop: '0.15rem',
                width: '16px',
                height: '16px',
                accentColor: 'var(--primary)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
            <span>
              {t('legal.termsAcceptRegister')}{' '}
              <Link to="/terms" target="_blank" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {t('footer.terms')}
              </Link>{' '}
              {t('legal.termsAnd')}{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {t('footer.privacy')}
              </Link>.{' '}
              {t('legal.ageRestriction')}
            </span>
          </label>

          <button
            type="submit"
            className="btn btn-primary"
            style={{
              width: '100%',
              opacity: termsAccepted ? 1 : 0.6,
              cursor: termsAccepted ? 'pointer' : 'not-allowed',
            }}
            disabled={loading || !termsAccepted}
          >
            {loading ? t('common.loading') : t('auth.register')}
          </button>
        </form>

        <div className="divider">{t('common.or')}</div>

        <button
          type="button"
          className="btn btn-google"
          style={{ width: '100%' }}
          onClick={handleGoogleSignup}
          disabled={loading}
        >
          <FcGoogle style={{ fontSize: '1.2rem' }} />
          {t('auth.googleLogin')}
        </button>

        <div className="auth-footer">
          {t('auth.hasAccount')}{' '}
          <Link to={returnTo !== '/' ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'}>
            {t('auth.login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
